import { useMemo } from 'react';
import { useQuery } from '@common-fe/common-fe';
import { capitalizeFirstLetter, formatSnakeCaseToGeneral } from '@common-fe/common-fe';
import { snakeCase, toString } from 'lodash';

import { api } from '@/api';
import { DefaultSortTypesEnum, Pagination } from '@/common/types';
import { API_URL } from '@/dtos';

import { CarrierConfigPurpose } from '../CarrierConfig.constants';

export interface CarrierConfigPayload {
  carrier_name: string;
  carrier_external_id: number;
  carrier_config_id: number;
  submitter_id: string;
  organization: string;
  purpose_type: CarrierConfigPurpose;
  description: string;
}

export interface CarrierConfig {
  carrierName: string;
  carrierExternalId: string;
  carrierConfigId: string;
  submitterId: string;
  organization: string;
  purposeType: string;
  description: string;
  statusType?: string;
}

interface Props {
  searchString?: string;
  purposes?: CarrierConfigPurpose[];
  statusType?: string;
  page?: number;
  perPage?: number;
  sortBy?: DefaultSortTypesEnum;
  enabled?: boolean;
  onSettled?: () => void;
}

const formatCarrierConfig = (value: CarrierConfigPayload): CarrierConfig => ({
  carrierName: value.carrier_name,
  carrierExternalId: toString(value.carrier_external_id),
  carrierConfigId: toString(value.carrier_config_id),
  submitterId: value.submitter_id,
  organization: value.organization,
  purposeType: capitalizeFirstLetter(formatSnakeCaseToGeneral(value.purpose_type)),
  description: value.description,
});

const useCarrierConfigListQuery = ({
  searchString = '',
  purposes,
  statusType,
  page,
  perPage,
  sortBy,
  enabled,
  onSettled,
}: Props) => {
  const purposeSearchString = purposes ? purposes.join(',') : '';

  const params = {
    search_value: searchString,
    purpose_types: purposes?.length
      ? purposes.map((purpose) => snakeCase(`${purpose}`).toUpperCase()).join(',')
      : '',
    status_type: statusType,
    page,
    ...(perPage ? { size: perPage } : {}),
    sort: sortBy && `carrierName,${sortBy}`,
  };

  const { isLoading, isSuccess, isError, data } = useQuery(
    [API_URL.CARRIER_CONFIGS, purposeSearchString, page, perPage, statusType, searchString, sortBy, enabled],

    // TODO: the DTO type is missing
    () => api.get<Pagination<CarrierConfigPayload>>(API_URL.CARRIER_CONFIGS, { params }),
    {
      enabled,
      onSuccess: onSettled,
    }
  );

  const carrierConfigsList = useMemo(() => {
    const list = data?.data?.content || [];
    return list.map((value) => formatCarrierConfig(value));
  }, [data]);

  const total = useMemo(() => data?.data?.totalElements || 0, [data]);

  return {
    formattedData: (carrierConfigsList || []) as CarrierConfig[],
    total,
    data,
    isLoading,
    isSuccess,
    isError,
  };
};

export default useCarrierConfigListQuery;
